import React from "react";
import { useRecoilValue } from "recoil";
import {
  userInfoState,
  unreadChatMessagesState,
  safeAreaInsetsState,
} from "../global-state";
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Tooltip,
} from "@mhome/ui";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AssistantIcon from "@mui/icons-material/Assistant";
import { useIsDarkMode } from "../lib/utils";

const VerticalNavigation = ({
  curTab,
  setCurTab,
  history,
  notificationStatus,
  notificationCount,
  onNotificationClick,
  isChatVisible,
  onChatToggle,
}) => {
  const userInfo = useRecoilValue(userInfoState);
  const unreadChatMessages = useRecoilValue(unreadChatMessagesState);
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const actualMode = useIsDarkMode();
  const isDark = actualMode === "dark";

  const navigationItems = [
    { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
    { label: "Automation", icon: <AutoModeIcon />, path: "/automation/list" },
    { label: "Inspiration", icon: <AutoAwesomeIcon />, path: "/gallery" },
    { label: "Menu", icon: <MenuIcon />, path: "/menu" },
  ];

  const handleTabChange = (index, path) => {
    setCurTab(index);
    history.push(path);
  };

  const handleProfileClick = () => {
    history.push("/setting");
  };

  const handleNotificationClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleChatToggle = () => {
    if (onChatToggle) {
      onChatToggle();
    }
  };

  const getNotificationColor = () => {
    if ((notificationStatus & 4) > 0) return "error";
    if ((notificationStatus & 2) > 0) return "warning";
    return "hsl(var(--foreground))";
  };

  // Sidebar (icons only)
  const Sidebar = () => (
    <Paper
      elevation={0}
      style={{
        position: "fixed",
        top: `${safeAreaInsets.top}px`,
        bottom: `${safeAreaInsets.bottom}px`,
        left: `${safeAreaInsets.left}px`,
        width: "60px",
        height: "auto",
        zIndex: 10,
        borderRadius: "0 16px 16px 0",
        overflow: "hidden",
        transition: "transform 0.3s ease",
        boxShadow: "none",
        backgroundColor: isDark
          ? "rgba(0, 0, 0, 0.4)"
          : "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(10px)",
      }}
    >
      {/* SVG gradient definition */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient
            id="assistantGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop
              offset="0%"
              style={{ stopColor: "#475B63", stopOpacity: 1 }}
            />
            <stop
              offset="100%"
              style={{ stopColor: "#E6A07C", stopOpacity: 1 }}
            />
          </linearGradient>
        </defs>
      </svg>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: "0 0 8px 0",
        }}
      >
        {/* Assistant icon at top */}
        <Tooltip title="Assistant" placement="right">
          <div
            onClick={handleChatToggle}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "40px",
              margin: "8px 8px 16px 8px",
              cursor: "pointer",
              borderRadius: "16px",
              background: isChatVisible
                ? "linear-gradient(135deg, #475B63 0%, #E6A07C 100%)"
                : "transparent",
              transition: "all 0.3s ease",
              boxShadow: isChatVisible
                ? "0 4px 12px rgba(230, 160, 124, 0.3)"
                : "none",
              transform: isChatVisible ? "scale(1.05)" : "scale(1)",
            }}
            onMouseEnter={(e) => {
              if (isChatVisible) {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #3a4d54 0%, #d9936b 100%)";
              } else {
                e.currentTarget.style.background = "hsl(var(--accent))";
              }
            }}
            onMouseLeave={(e) => {
              if (isChatVisible) {
                e.currentTarget.style.background =
                  "linear-gradient(135deg, #475B63 0%, #E6A07C 100%)";
              } else {
                e.currentTarget.style.background = "transparent";
              }
            }}
          >
            {unreadChatMessages > 0 ? (
              <Badge badgeContent={unreadChatMessages} color="error" max={99}>
                <AssistantIcon
                  style={{
                    fontSize: "28px",
                    color: isChatVisible
                      ? "#ffffff"
                      : "url(#assistantGradient)",
                    fill: !isChatVisible
                      ? "url(#assistantGradient)"
                      : undefined,
                    transition: "all 0.3s ease",
                  }}
                />
              </Badge>
            ) : (
              <AssistantIcon
                style={{
                  fontSize: "28px",
                  color: isChatVisible ? "#ffffff" : "url(#assistantGradient)",
                  fill: !isChatVisible ? "url(#assistantGradient)" : undefined,
                  transition: "all 0.3s ease",
                }}
              />
            )}
          </div>
        </Tooltip>

        {/* Navigation items - icons only */}
        <List style={{ padding: 0, flex: 1 }}>
          {navigationItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <Tooltip title={item.label} placement="right">
                <ListItemButton
                  selected={curTab === index}
                  onClick={() => handleTabChange(index, item.path)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "2px 8px",
                    padding: "8px 12px",
                    borderRadius: "16px",
                    height: "40px",
                  }}
                >
                  <ListItemIcon
                    style={{
                      minWidth: "auto",
                      display: "flex",
                      justifyContent: "center",
                      margin: 0,
                    }}
                  >
                    {React.cloneElement(item.icon, {
                      style: {
                        fontSize: "20px",
                        color:
                          curTab === index
                            ? "hsl(var(--primary-foreground))"
                            : "hsl(var(--foreground))",
                      },
                    })}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        {/* Bottom section with notification and profile */}
        <div
          style={{
            padding: "8px",
          }}
        >
          {/* Notification icon */}
          <Tooltip title="Notifications" placement="right">
            <IconButton
              onClick={handleNotificationClick}
              style={{
                width: "44px",
                height: "44px",
                margin: "0 auto 8px auto",
                display: "flex",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {notificationCount > 0 ? (
                <Badge
                  badgeContent={notificationCount}
                  color="warning"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  max={99}
                >
                  <NotificationsIcon
                    style={{
                      fontSize: "22px",
                      color: getNotificationColor(),
                    }}
                  />
                </Badge>
              ) : (
                <NotificationsIcon
                  style={{
                    fontSize: "22px",
                    color: getNotificationColor(),
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          {/* User profile avatar */}
          <Tooltip title="Settings" placement="right">
            <IconButton
              onClick={handleProfileClick}
              style={{
                width: "44px",
                height: "44px",
                margin: "0 auto",
                display: "flex",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <Avatar
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor:
                    "hsl(var(--primary))" || "hsl(var(--primary))",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                {userInfo?.name?.charAt(0).toUpperCase() || "U"}
              </Avatar>
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </Paper>
  );

  return <Sidebar />;
};

export default VerticalNavigation;
